import { Fragment, memo, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import {
  ColumnDef,
  ColumnSizingState,
  flexRender,
  getCoreRowModel,
  Row,
  RowData,
  Table as TanstackTable,
  useReactTable,
} from '@tanstack/react-table'
import { AuditEntry } from '../../io/api/audit/data/AuditEntry'
import { renderChangeDetail, renderChangeSummary } from './changeRendering'
import EntityLink from '../../components/data/EntityLink'
import TargetLink from './TargetLink'
import { actionEmoji } from './actionEmoji'

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface TableMeta<TData extends RowData> {
    isOpen: (id: string) => boolean
    toggle: (id: string) => void
  }
}

const formatTimestamp = (iso: string) => new Date(iso).toLocaleString()

type Props = {
  entries: AuditEntry[]
  loading: boolean
  error: Error | null
  hasMore: boolean
  loadMore: () => void
}

const COLUMNS: ColumnDef<AuditEntry>[] = [
  {
    id: 'expand',
    header: '',
    size: 48,
    minSize: 48,
    enableResizing: false,
    cell: ({ row, table }) => {
      const hasDetail = !!row.original.changes && row.original.changes.length > 0
      if (!hasDetail) return null
      const isOpen = table.options.meta!.isOpen(row.original.id)
      return (
        <IconButton size="small" onClick={() => table.options.meta!.toggle(row.original.id)}>
          {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
      )
    },
  },
  {
    id: 'when',
    header: 'When',
    size: 180,
    minSize: 120,
    cell: ({ row }) => formatTimestamp(row.original.timestamp),
  },
  {
    id: 'target',
    header: 'Target',
    size: 320,
    minSize: 140,
    cell: ({ row }) => <TargetLink entry={row.original} />,
  },
  {
    id: 'actor',
    header: 'Actor',
    size: 220,
    minSize: 120,
    cell: ({ row }) => <EntityLink entityId={row.original.actorUserEntityId} />,
  },
  {
    id: 'action',
    header: 'Action',
    size: 200,
    minSize: 140,
    cell: ({ row }) => <Chip size="small" label={`${actionEmoji(row.original.action)} ${row.original.action}`} />,
  },
  {
    id: 'changes',
    header: 'Changes',
    size: 480,
    minSize: 200,
    cell: ({ row }) => renderChangeSummary(row.original),
  },
]

const cellInnerSx = (columnId: string) =>
  columnId === 'changes'
    ? { whiteSpace: 'normal' as const, wordBreak: 'break-word' as const, overflowWrap: 'anywhere' as const }
    : { whiteSpace: 'nowrap' as const, overflow: 'hidden' as const, textOverflow: 'ellipsis' as const }

type RowsProps = {
  rows: Row<AuditEntry>[]
  expanded: Record<string, boolean>
  visibleColumnCount: number
  loading: boolean
}

const Rows = memo(({ rows, expanded, visibleColumnCount, loading }: RowsProps) => (
  <>
    {rows.map((row) => {
      const hasDetail = !!row.original.changes && row.original.changes.length > 0
      const isOpen = !!expanded[row.original.id]
      const cells = row.getVisibleCells()
      return (
        <Fragment key={row.id}>
          <TableRow hover sx={{ display: 'flex', width: '100%' }}>
            {cells.map((cell, index) => (
              <TableCell
                key={cell.id}
                sx={{
                  width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
                  flexGrow: index === cells.length - 1 ? 1 : 0,
                  flexShrink: 0,
                  minWidth: 0,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0, ...cellInnerSx(cell.column.id) }}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Box>
              </TableCell>
            ))}
          </TableRow>
          {hasDetail && (
            <TableRow sx={{ display: 'flex', width: '100%' }}>
              <TableCell sx={{ flex: 1, py: 0, border: isOpen ? undefined : 0 }}>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <Box py={1}>{renderChangeDetail(row.original)}</Box>
                </Collapse>
              </TableCell>
            </TableRow>
          )}
        </Fragment>
      )
    })}
    {rows.length === 0 && !loading && (
      <TableRow sx={{ display: 'flex', width: '100%' }}>
        <TableCell colSpan={visibleColumnCount} sx={{ flex: 1, textAlign: 'center', border: 'none' }}>
          <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
            No audit entries.
          </Typography>
        </TableCell>
      </TableRow>
    )}
  </>
))
Rows.displayName = 'AuditLogRows'

const useColumnSizeVars = (table: TanstackTable<AuditEntry>): Record<string, number> => {
  const sizingInfo = table.getState().columnSizingInfo
  const sizing = table.getState().columnSizing
  return useMemo(() => {
    const headers = table.getFlatHeaders()
    const vars: Record<string, number> = {}
    headers.forEach((header) => {
      vars[`--col-${header.column.id}-size`] = header.getSize()
    })
    return vars
  }, [table, sizingInfo, sizing])
}

const AuditLogTable = ({ entries, loading, error, hasMore, loadMore }: Props) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({})

  const tableMeta = useMemo(
    () => ({
      isOpen: (id: string) => !!expanded[id],
      toggle: (id: string) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] })),
    }),
    [expanded]
  )

  const table = useReactTable({
    data: entries,
    columns: COLUMNS,
    state: { columnSizing },
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
    meta: tableMeta,
  })

  const columnSizeVars = useColumnSizeVars(table)

  useEffect(() => {
    const node = sentinelRef.current
    if (!node) return
    const observer = new IntersectionObserver(
      (intersection) => {
        if (intersection[0]?.isIntersecting && hasMore && !loading) loadMore()
      },
      { rootMargin: '256px' }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, loading, loadMore])

  if (error) return <Alert severity="error">{error.message}</Alert>

  const rows = table.getRowModel().rows
  const headerGroups = table.getHeaderGroups()
  const visibleColumnCount = table.getVisibleFlatColumns().length
  return (
    <Stack spacing={1} sx={{ width: '100%' }}>
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <Table
          size="small"
          style={columnSizeVars as React.CSSProperties}
          sx={{ tableLayout: 'fixed', minWidth: 'max-content', display: 'grid', width: '100%' }}
        >
          <TableHead sx={{ display: 'grid' }}>
            {headerGroups.map((hg) => (
              <TableRow key={hg.id} sx={{ display: 'flex', backgroundColor: 'background.paper', minWidth: '100%' }}>
                {hg.headers.map((header, index) => (
                  <TableCell
                    key={header.id}
                    sx={{
                      width: `calc(var(--col-${header.column.id}-size) * 1px)`,
                      flexGrow: index === hg.headers.length - 1 ? 1 : 0,
                      flexShrink: 0,
                      position: 'relative',
                      backgroundColor: 'inherit',
                      fontWeight: 'bold',
                      py: 1,
                      display: 'flex',
                      alignItems: 'center',
                      overflow: 'hidden',
                      borderBottom: 1,
                      borderColor: 'divider',
                    }}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanResize() && (
                      <Box
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        sx={{
                          position: 'absolute',
                          right: 1,
                          top: '25%',
                          height: '50%',
                          width: 2,
                          cursor: 'col-resize',
                          userSelect: 'none',
                          touchAction: 'none',
                          borderRadius: 0.5,
                          backgroundColor: header.column.getIsResizing() ? 'primary.main' : 'divider',
                          '&:hover': { backgroundColor: 'primary.main' },
                        }}
                      />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody sx={{ display: 'grid' }}>
            <Rows rows={rows} expanded={expanded} visibleColumnCount={visibleColumnCount} loading={loading} />
          </TableBody>
        </Table>
      </Box>
      <Box ref={sentinelRef} display="flex" justifyContent="center" py={2}>
        {loading && <CircularProgress size={20} />}
      </Box>
    </Stack>
  )
}

export default AuditLogTable
